import type { CryptoSwapsTranslations } from "@/shared/lang/interfaces/cryptoSwaps";

import type { OtcAmountAnchor } from "../types/cryptoSwaps.types";
import type { AmountBounds, AmountCheck } from "./cryptoSwapAmounts";
import {
  AMOUNT_ABOVE_MAXIMUM_CODE,
  AMOUNT_BELOW_MINIMUM_CODE,
  cryptoSwapErrorText,
  isRangeRejection,
  type NormalizedCryptoSwapError,
} from "./cryptoSwapErrors";

/** What is wrong with one amount field, in a shape both the field and the review button render from. */
export type AmountIssue =
  | { kind: "invalid"; side: OtcAmountAnchor }
  | { kind: "precision"; side: OtcAmountAnchor; decimals: number }
  | { kind: "below_min" | "above_max"; side: OtcAmountAnchor; amount: string; currency: string }
  | { kind: "out_of_range"; side: OtcAmountAnchor; min: string; max: string; currency: string }
  | { kind: "server"; side: OtcAmountAnchor; error: NormalizedCryptoSwapError }
  | { kind: "balance"; side: "send"; available: string; currency: string }
  | { kind: "no_wallet"; side: "send"; currency: string };

export interface AmountIssueContext {
  /** The field the user typed in: the only one whose limits apply. */
  side: OtcAmountAnchor;
  sendCurrency: string;
  receiveCurrency: string;
  sendDecimals: number;
  receiveDecimals: number;
  sendBounds: AmountBounds | null;
  receiveBounds: AmountBounds | null;
}

type RangeKind = "below_min" | "above_max";

function sideLimits(context: AmountIssueContext): { bounds: AmountBounds | null; currency: string } {
  return context.side === "send"
    ? { bounds: context.sendBounds, currency: context.sendCurrency }
    : { bounds: context.receiveBounds, currency: context.receiveCurrency };
}

function rangeIssue(kind: RangeKind, context: AmountIssueContext): AmountIssue | null {
  const { bounds, currency } = sideLimits(context);
  const amount = bounds?.[kind === "below_min" ? "min" : "max"] ?? null;
  return amount ? { kind, side: context.side, amount, currency } : null;
}

/** The anchored field's problem as the client sees it, before anything is priced. */
export function clientAmountIssue(check: AmountCheck, context: AmountIssueContext): AmountIssue | null {
  const { side } = context;
  if (check === "invalid") return { kind: "invalid", side };
  if (check === "precision") {
    return {
      kind: "precision",
      side,
      decimals: side === "send" ? context.sendDecimals : context.receiveDecimals,
    };
  }
  if (check === "below_min" || check === "above_max") return rangeIssue(check, context);
  return null;
}

/** A range refusal from pricing, worded from the anchored side's own limits; the server's text otherwise. */
export function serverAmountIssue(
  error: NormalizedCryptoSwapError | null,
  context: AmountIssueContext,
): AmountIssue | null {
  if (!error || !isRangeRejection(error)) return null;
  const server: AmountIssue = { kind: "server", side: context.side, error };
  if (error.code === AMOUNT_BELOW_MINIMUM_CODE) return rangeIssue("below_min", context) ?? server;
  if (error.code === AMOUNT_ABOVE_MAXIMUM_CODE) return rangeIssue("above_max", context) ?? server;
  const { bounds, currency } = sideLimits(context);
  return bounds?.min && bounds.max
    ? { kind: "out_of_range", side: context.side, min: bounds.min, max: bounds.max, currency }
    : server;
}

/** Zero is a prompt, not a mistake: it reads as guidance while "0,5" is still being typed. */
export function isFieldError(issue: AmountIssue | null): boolean {
  return !!issue && issue.kind !== "invalid";
}

export type IssueTranslate = (
  key: keyof CryptoSwapsTranslations,
  options?: Record<string, unknown>,
) => string;

export type AmountRangeKeys = Record<
  OtcAmountAnchor,
  Record<RangeKind | "out_of_range", keyof CryptoSwapsTranslations>
>;

export const BUY_RANGE_KEYS: AmountRangeKeys = {
  send: { below_min: "amount_below_min", above_max: "amount_above_max", out_of_range: "amount_out_of_range" },
  receive: {
    below_min: "receive_below_min",
    above_max: "receive_above_max",
    out_of_range: "receive_out_of_range",
  },
};

/** The sale's own limits are per sale; what to receive reads the same either way. */
export const SELL_RANGE_KEYS: AmountRangeKeys = {
  send: {
    below_min: "sell_amount_below_min",
    above_max: "sell_amount_above_max",
    out_of_range: "sell_amount_out_of_range",
  },
  receive: BUY_RANGE_KEYS.receive,
};

/** One sentence per issue, shared by the field and the disabled review button so they never disagree. */
export function amountIssueMessage(
  issue: AmountIssue,
  translate: IssueTranslate,
  formatBound: (amount: string, currency: string) => string,
  rangeKeys: AmountRangeKeys = BUY_RANGE_KEYS,
): string {
  switch (issue.kind) {
    case "invalid":
      return translate("amount_invalid");
    case "precision":
      return translate("amount_precision", { decimals: issue.decimals });
    case "below_min":
    case "above_max":
      return translate(rangeKeys[issue.side][issue.kind], {
        amount: formatBound(issue.amount, issue.currency),
        currency: issue.currency,
      });
    case "out_of_range":
      return translate(rangeKeys[issue.side].out_of_range, {
        min: formatBound(issue.min, issue.currency),
        max: formatBound(issue.max, issue.currency),
        currency: issue.currency,
      });
    case "server":
      return cryptoSwapErrorText(issue.error, translate);
    case "balance":
      return translate("balance_insufficient", {
        amount: formatBound(issue.available, issue.currency),
        currency: issue.currency,
      });
    case "no_wallet":
      return translate("balance_missing", { currency: issue.currency });
  }
}
